import Styles from './ImageVariant.module.scss'
import { forwardRef, PropsWithChildren, useCallback, useEffect, useState } from 'react'
import cx from 'classnames'
import { Skeleton } from 'components/Skeleton/Skeleton'
import { ReplaceImageButton } from './ReplaceImageButton'
import { ImageVariant as IImageVariant } from 'stores/ImageStore'
import { FocusScope } from '@react-aria/focus'
import { ImageNodeAttrs } from 'common/Prose/nodeViews/Image/Types'
import { EditorView } from 'prosemirror-view'
import { AdvancedImage } from '@cloudinary/react'
import { fit as fitImage } from '@cloudinary/url-gen/actions/resize'
import { useCloudinary } from 'providers/CloudinaryProvider/CloudinaryProvider'

interface Props {
  variant: IImageVariant
  attrs: ImageNodeAttrs
  view: EditorView
  isLoading?: boolean
}

const ImageWithRetry = forwardRef((props: any, fRef) => {
  const [retries, setRetries] = useState(5)

  const handleError = useCallback(() => {
    if (retries >= 0) {
      const timeout = setTimeout(() => setRetries((currentRetries) => Math.max(currentRetries - 1, 0)), 5000)
      return () => {
        clearTimeout(timeout)
      }
    }
  }, [retries])

  return <AdvancedImage ref={fRef} key={`retries-${retries}`} {...props} onError={handleError} />
})

export const ImageVariant = ({ variant, children, isLoading = false, ...props }: PropsWithChildren<Props>) => {
  const { image } = variant
  const cloudinary = useCloudinary()
  const url = variant.url
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
  }, [url])

  if (!cloudinary) return null

  // @ts-ignore
  const newImage = cloudinary.image(image.publicID).setDeliveryType(image.deliveryType)
  newImage.addTransformation(image.toString())

  return (
    <FocusScope autoFocus>
      <div
        className={cx(Styles.container, { [Styles.loading]: loading || isLoading })}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={Styles.controls}>
          <ReplaceImageButton url={url} {...props} />
        </div>
        {children && <div className={Styles.qualifierContainer}>{children}</div>}
        <div className={Styles.imageContainer}>
          <ImageWithRetry
            cldImg={newImage.resize(fitImage().width(400))}
            className={Styles.image}
            onLoad={() => setLoading(false)}
          />
          <Skeleton active paragraph={false} className={Styles.skeleton} />
        </div>
      </div>
    </FocusScope>
  )
}
